import { useState } from "react";
import { toast } from "sonner";
import { PageHero, PrimaryButton } from "@/components/ui-bits";
import { SERVICE_OPTIONS } from "@/content/site";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-tark-line bg-tark-card px-4 py-3 text-sm text-tark-ink placeholder:text-tark-dim focus:border-tark-cyan/60 focus:outline-none focus:ring-1 focus:ring-tark-cyan/40";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-tark-dim";

const PROJECT_TYPES = ["New Product / Platform", "Modernization / Migration", "Team Augmentation", "Managed Services", "Consulting / Advisory", "Other"];
const BUDGETS = ["Under $25k", "$25k – $75k", "$75k – $150k", "$150k – $500k", "$500k+", "To be discussed"];

const StartProject = () => {
  const [form, setForm] = useState({
    name: "", work_email: "", company: "", job_title: "", country: "", phone: "",
    service_required: "", project_type: "", estimated_budget: "", expected_start_date: "",
    project_description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="For Business Clients — Start a Project"
        lines={["Tell Us What", "You Need Built."]}
        sub="Share your requirements and a TARK engineering lead will review them. This form is for project and service inquiries — if you want to work at TARK, head to Careers."
      />
      <section className="mx-auto max-w-4xl px-6 pb-28 lg:px-10" data-testid="start-project-page">
        {done ? (
          <div className="rounded-2xl border border-tark-cyan/40 bg-tark-card p-12 text-center" data-testid="lead-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-tark-cyan/40 bg-tark-cyan/10">
              <span className="h-3 w-3 rotate-45 bg-tark-cyan" />
            </div>
            <h2 className="mt-6 font-display text-2xl font-bold">Thank you for contacting TARK.</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-tark-mut">
              Our team will review your requirements and get back to you.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-tark-line bg-tark-surface/50 p-8 sm:p-12" data-testid="start-project-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={labelCls}>Name *</label>
                <input id="name" name="name" required value={form.name} onChange={set} className={inputCls} data-testid="lead-name" />
              </div>
              <div>
                <label htmlFor="work_email" className={labelCls}>Work Email *</label>
                <input id="work_email" name="work_email" type="email" required value={form.work_email} onChange={set} className={inputCls} data-testid="lead-work-email" />
              </div>
              <div>
                <label htmlFor="company" className={labelCls}>Company</label>
                <input id="company" name="company" value={form.company} onChange={set} className={inputCls} data-testid="lead-company" />
              </div>
              <div>
                <label htmlFor="job_title" className={labelCls}>Job Title</label>
                <input id="job_title" name="job_title" value={form.job_title} onChange={set} className={inputCls} data-testid="lead-job-title" />
              </div>
              <div>
                <label htmlFor="country" className={labelCls}>Country</label>
                <input id="country" name="country" value={form.country} onChange={set} className={inputCls} data-testid="lead-country" />
              </div>
              <div>
                <label htmlFor="phone" className={labelCls}>Phone</label>
                <input id="phone" name="phone" type="tel" value={form.phone} onChange={set} className={inputCls} data-testid="lead-phone" />
              </div>
              <div>
                <label htmlFor="service_required" className={labelCls}>Service Required *</label>
                <select id="service_required" name="service_required" required value={form.service_required} onChange={set}
                        className={inputCls} data-testid="lead-service-required">
                  <option value="">Select a service…</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="project_type" className={labelCls}>Project Type</label>
                <select id="project_type" name="project_type" value={form.project_type} onChange={set}
                        className={inputCls} data-testid="lead-project-type">
                  <option value="">Select…</option>
                  {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="estimated_budget" className={labelCls}>Estimated Budget</label>
                <select id="estimated_budget" name="estimated_budget" value={form.estimated_budget} onChange={set}
                        className={inputCls} data-testid="lead-budget">
                  <option value="">Select…</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="expected_start_date" className={labelCls}>Expected Start Date</label>
                <input id="expected_start_date" name="expected_start_date" type="date" value={form.expected_start_date} onChange={set}
                       className={`${inputCls} [color-scheme:dark]`} data-testid="lead-start-date" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="project_description" className={labelCls}>Project Description</label>
                <textarea id="project_description" name="project_description" rows={5} value={form.project_description} onChange={set}
                          className={inputCls} placeholder="What are you building or changing? What does success look like?"
                          data-testid="lead-description" />
              </div>
            </div>
            <div className="mt-9">
              <PrimaryButton type="submit" testId="lead-submit">
                {submitting ? "Sending…" : "Request Consultation"}
              </PrimaryButton>
            </div>
          </form>
        )}
      </section>
    </>
  );
};

export default StartProject;
